import type {
  DelegatedCatalogInfo,
  ManageableCatalogInfo,
  OwnedCatalogInfo,
} from '../contexts/ActiveCatalogContext';

/**
 * Compute the catalogs a user may create/manage albums in.
 *
 * Albums are keyed by OWNER, so the user's own catalogs (primary + add-ons)
 * collapse into a single entry (`ownerUserId === null`), and each owner the
 * user holds an active, non-expired `full` delegation from appears exactly once.
 *
 * View-only and expired delegations are excluded. The expiry check is
 * per-delegation-row: an expired `full` row is dropped even if the same owner
 * has another active (e.g. view-only) row — otherwise the dialog would offer a
 * pool the backend rejects on submit.
 *
 * @param nowMs current time in ms (passed in so this stays a pure function the
 *   caller can test deterministically and React can call outside render purity).
 */
export function computeManageableCatalogs(
  ownedCatalogs: OwnedCatalogInfo[],
  delegatedCatalogs: DelegatedCatalogInfo[],
  nowMs: number,
): ManageableCatalogInfo[] {
  const result: ManageableCatalogInfo[] = [];

  // The own pool always represents the user's own catalog(s) collectively and
  // switches to the primary (catalogAliId null), so label it from the PRIMARY's
  // name. If the list somehow has no primary row, fall back to a generic label
  // rather than borrowing an add-on's name (which would mislabel the pool).
  if (ownedCatalogs.length > 0) {
    const primary = ownedCatalogs.find((c) => c.isPrimary);
    result.push({
      catalogAliId: null,
      label: primary?.creatorName ?? 'My Catalog',
      ownerUserId: null,
      kind: 'own',
    });
  }

  const seenOwners = new Set<string>();
  for (const d of delegatedCatalogs) {
    if (d.permissions !== 'full') continue;
    const expiresAt = Date.parse(d.expiresAt);
    if (!Number.isFinite(expiresAt) || expiresAt <= nowMs) continue;
    if (seenOwners.has(d.delegatorUserId)) continue;
    seenOwners.add(d.delegatorUserId);
    result.push({
      catalogAliId: d.delegatorAliId,
      label: d.delegatorName ?? d.delegatorAliId,
      ownerUserId: d.delegatorUserId,
      kind: 'delegated',
    });
  }
  return result;
}
